<script setup>
// BUILDER-09/10 #3: an n8n-like value field. Two display states over a plain string (the
// compiler consumes strings, so there is no document model):
//   • DISPLAY — tokens render as coloured pills; hovering a pill previews name/kind/description.
//     It is ALSO a drop target so a chip dragged from the Variables panel lands here (#3c).
//   • EDIT (on click) — a real <input>/<textarea> for reliable caret/paste/backspace, with a
//     "{{" typeahead and caret-accurate drop.
// Two token kinds — both PINK (n-brand), told apart by ICON, not colour (#3d):
//   • context var  {{name}}  — braces icon — system/conversation value (BUILDER-07 #3c).
//   • AI value     {name}    — sparkles icon — filled by the model; compiles to $fromAI.
// Creating/editing AI values now lives in the shared Variables panel (ToolVariablesPanel);
// this field only renders + inserts tokens.
import { ref, reactive, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  varToken,
  aiToken,
  parseTokens,
  BUILTINS,
} from '../powerAgents/variableTokens';

const props = defineProps({
  modelValue: { type: String, default: '' },
  // [{ name, kind: 'system' | 'declared' }]
  variables: { type: Array, default: () => [] },
  // The tool's AI params (form.params rows): [{ key, description, ... }] — source of a pill's
  // hover description.
  aiParams: { type: Array, default: () => [] },
  multiline: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  // Class string of the field being replaced, so the form looks unchanged.
  inputClass: { type: String, default: '' },
  // Extra classes for the wrapper (e.g. `flex-1` inside a flex row).
  wrapperClass: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const inputRef = ref(null);
const wrapperRef = ref(null);
const editing = ref(false);
const dragOver = ref(false);
const segments = computed(() => parseTokens(props.modelValue));

// ── pill metadata ────────────────────────────────────────────────
function aiDescription(name) {
  const p = props.aiParams.find(x => (x.key || x.name) === name);
  return p?.description || '';
}
function varKind(name) {
  const v = props.variables.find(x => x.name === name);
  if (v) return v.kind;
  return BUILTINS.includes(name) ? 'system' : 'declared';
}

// ── hover preview ────────────────────────────────────────────────
const hover = reactive({
  open: false,
  name: '',
  kind: '',
  description: '',
  x: 0,
  y: 0,
});
function showPreview(seg, e) {
  const box = e.currentTarget.getBoundingClientRect();
  hover.open = true;
  hover.name = seg.name;
  hover.description = seg.type === 'ai' ? aiDescription(seg.name) : '';
  hover.kind =
    seg.type === 'ai'
      ? t('POWER_AGENTS.TOOLS.FORM.VARIABLE_AI')
      : t(
          varKind(seg.name) === 'system'
            ? 'POWER_AGENTS.TOOLS.FORM.VARIABLE_SYSTEM'
            : 'POWER_AGENTS.TOOLS.FORM.VARIABLE_DECLARED'
        );
  hover.x = box.left;
  hover.y = box.bottom + 4;
}
function hidePreview() {
  hover.open = false;
}

// ── edit-mode entry / exit ───────────────────────────────────────
function enterEdit() {
  editing.value = true;
  nextTick(() => {
    const el = inputRef.value;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  });
}
function setCaret(pos) {
  const el = inputRef.value;
  nextTick(() => {
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  });
}

// ── "{{" typeahead ───────────────────────────────────────────────
const ta = reactive({ open: false, query: '', start: -1, index: 0 });
const filteredVars = computed(() => {
  const q = ta.query.toLowerCase();
  return props.variables.filter(v => v.name.toLowerCase().includes(q));
});
function closeTypeahead() {
  ta.open = false;
  ta.query = '';
  ta.start = -1;
  ta.index = 0;
}
function detectTypeahead() {
  const el = inputRef.value;
  if (!el) return closeTypeahead();
  const before = el.value.slice(0, el.selectionStart);
  const open = before.lastIndexOf('{{');
  if (open === -1) return closeTypeahead();
  const frag = before.slice(open + 2);
  if (
    frag.includes('}}') ||
    frag.includes('{') ||
    !/^[a-z0-9_]*$/i.test(frag)
  ) {
    return closeTypeahead();
  }
  ta.open = true;
  ta.query = frag;
  ta.start = open;
  if (ta.index >= filteredVars.value.length) ta.index = 0;
  return undefined;
}
function onInput(e) {
  emit('update:modelValue', e.target.value);
  nextTick(detectTypeahead);
}
function onKeyup(e) {
  if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key))
    detectTypeahead();
}
function chooseVar(variable) {
  const el = inputRef.value;
  const token = varToken(variable.name);
  const next =
    el.value.slice(0, ta.start) + token + el.value.slice(el.selectionStart);
  const pos = ta.start + token.length;
  emit('update:modelValue', next);
  closeTypeahead();
  setCaret(pos);
}
function onKeydown(e) {
  if (!ta.open || !filteredVars.value.length) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    ta.index = (ta.index + 1) % filteredVars.value.length;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    ta.index =
      (ta.index - 1 + filteredVars.value.length) % filteredVars.value.length;
  } else if (e.key === 'Enter') {
    e.preventDefault();
    chooseVar(filteredVars.value[ta.index]);
  } else if (e.key === 'Escape') {
    e.preventDefault();
    closeTypeahead();
  }
}

function onFocusOut() {
  // Entering edit swaps the display div for the input; that unmount fires a transient
  // focusout with a null relatedTarget BEFORE the input focuses (nextTick). Defer the check
  // to a macrotask so the input has focus by then — otherwise we'd flip straight back out.
  setTimeout(() => {
    const el = wrapperRef.value;
    if (el && el.contains(document.activeElement)) return;
    editing.value = false;
    closeTypeahead();
  }, 0);
}

// ── drop target (chips are dragged from ToolVariablesPanel, #3c) ──
function caretFromPoint(el, x, y) {
  let offset = null;
  if (document.caretPositionFromPoint) {
    const c = document.caretPositionFromPoint(x, y);
    if (c) offset = c.offset;
  } else if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y);
    if (r) offset = r.startOffset;
  }
  if (offset == null) offset = el.selectionStart ?? el.value.length;
  return Math.min(Math.max(offset, 0), el.value.length);
}
function onDropEdit(e) {
  const token = e.dataTransfer.getData('text/plain');
  dragOver.value = false;
  if (!token) return; // not ours — let the browser handle it
  e.preventDefault();
  const el = inputRef.value;
  const pos = caretFromPoint(el, e.clientX, e.clientY);
  const v = props.modelValue ?? '';
  emit('update:modelValue', v.slice(0, pos) + token + v.slice(pos));
  setCaret(pos + token.length);
}
function onDropDisplay(e) {
  const token = e.dataTransfer.getData('text/plain');
  dragOver.value = false;
  if (!token) return;
  e.preventDefault();
  // No reliable caret inside the pill div — append to the end (predictable landing).
  emit('update:modelValue', (props.modelValue ?? '') + token);
}
</script>

<template>
  <div
    ref="wrapperRef"
    class="relative"
    :class="wrapperClass"
    @focusout="onFocusOut"
  >
    <!-- DISPLAY: coloured pills + hover preview. Click to edit; drop to append a token. -->
    <div
      v-if="!editing"
      :class="[
        inputClass,
        dragOver ? 'ring-2 ring-n-brand/60 border-n-brand' : '',
      ]"
      class="cursor-text whitespace-pre-wrap break-all min-h-[2.25rem] transition-shadow"
      role="textbox"
      tabindex="0"
      @click="enterEdit"
      @focus="enterEdit"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop="onDropDisplay"
    >
      <template v-if="segments.length">
        <template v-for="(seg, i) in segments" :key="i">
          <span v-if="seg.type === 'text'">{{ seg.value }}</span>
          <span
            v-else
            class="inline-flex items-center gap-0.5 px-1.5 rounded font-mono cursor-pointer align-baseline bg-n-brand/10 text-n-brand border border-n-brand/40"
            @mouseenter="showPreview(seg, $event)"
            @mouseleave="hidePreview"
          >
            <i
              :class="
                seg.type === 'ai' ? 'i-lucide-sparkles' : 'i-lucide-braces'
              "
              class="text-[0.85em]"
            />
            {{ seg.type === 'ai' ? aiToken(seg.name) : varToken(seg.name) }}
          </span>
        </template>
      </template>
      <span v-else class="text-n-slate-9">{{ placeholder }}</span>
    </div>

    <!-- EDIT: reliable textarea/input for typing -->
    <textarea
      v-else-if="multiline"
      ref="inputRef"
      :value="modelValue"
      :placeholder="placeholder"
      rows="2"
      :class="[inputClass, dragOver ? 'ring-2 ring-n-brand/60' : '']"
      @input="onInput"
      @keydown="onKeydown"
      @keyup="onKeyup"
      @click="detectTypeahead"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop="onDropEdit"
    />
    <input
      v-else-if="editing"
      ref="inputRef"
      type="text"
      :value="modelValue"
      :placeholder="placeholder"
      :class="[inputClass, dragOver ? 'ring-2 ring-n-brand/60' : '']"
      @input="onInput"
      @keydown="onKeydown"
      @keyup="onKeyup"
      @click="detectTypeahead"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop="onDropEdit"
    />

    <!-- "{{" typeahead popup -->
    <ul
      v-if="editing && ta.open && filteredVars.length"
      class="absolute left-0 z-50 flex flex-col w-56 gap-0.5 p-1 mt-1 overflow-y-auto border rounded-md shadow-lg top-full max-h-48 border-n-weak bg-n-solid-1"
    >
      <li
        v-for="(variable, i) in filteredVars"
        :key="variable.name"
        class="flex items-center gap-2 px-2 py-1 text-xs rounded cursor-pointer"
        :class="
          i === ta.index ? 'bg-n-alpha-2 text-n-slate-12' : 'text-n-slate-11'
        "
        @mousedown.prevent="chooseVar(variable)"
        @mouseenter="ta.index = i"
      >
        <i class="i-lucide-braces text-n-brand" />
        <code class="font-mono text-n-brand">{{
          varToken(variable.name)
        }}</code>
        <span
          v-if="variable.kind === 'system'"
          class="ml-auto text-[10px] px-1 rounded bg-n-alpha-2 text-n-slate-10"
        >
          {{ t('POWER_AGENTS.TOOLS.FORM.VARIABLE_SYSTEM') }}
        </span>
      </li>
    </ul>

    <!-- hover preview -->
    <div
      v-if="hover.open"
      class="fixed z-[60] max-w-xs px-2.5 py-1.5 text-xs border rounded-md shadow-lg pointer-events-none border-n-weak bg-n-solid-1"
      :style="{ left: `${hover.x}px`, top: `${hover.y}px` }"
    >
      <div class="flex items-center gap-2">
        <code class="font-mono text-n-slate-12">{{ hover.name }}</code>
        <span class="text-[10px] px-1 rounded bg-n-alpha-2 text-n-slate-10">
          {{ hover.kind }}
        </span>
      </div>
      <p v-if="hover.description" class="mt-1 text-n-slate-11">
        {{ hover.description }}
      </p>
    </div>
  </div>
</template>
